import React, { useEffect, useRef } from 'react'

interface FireworksProps {
  isActive: boolean
  onComplete?: () => void
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  color: string
  size: number
}

interface Firework {
  x: number
  y: number
  targetY: number
  vx: number
  vy: number
  exploded: boolean
  particles: Particle[]
  color: string
  age: number // Track how long the firework has been alive
  fallingStartTime: number | null // Track when firework started falling
}

const Fireworks: React.FC<FireworksProps> = ({ isActive, onComplete }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>()
  const fireworksRef = useRef<Firework[]>([])
  const lastFireworkTime = useRef<number>(0)

  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
    '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
  ]

  const createParticle = (x: number, y: number, color: string): Particle => {
    const angle = Math.random() * Math.PI * 2
    const speed = Math.random() * 6 + 2
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 60,
      maxLife: 60,
      color,
      size: Math.random() * 3 + 1
    }
  }

  const createFirework = (canvas: HTMLCanvasElement): Firework => {
    const startX = Math.random() * canvas.width
    const startY = canvas.height
    const targetY = Math.random() * (canvas.height * 0.4) + canvas.height * 0.1
    const color = colors[Math.floor(Math.random() * colors.length)]

    // Calculate required velocity to reach target height with gravity
    const distance = startY - targetY
    const minVelocity = Math.sqrt(2 * 0.2 * distance) // v = sqrt(2 * gravity * distance)

    return {
      x: startX,
      y: startY,
      targetY,
      vx: (Math.random() - 0.5) * 3, // Slightly more horizontal variation
      vy: -(minVelocity + Math.random() * 4), // Ensure enough velocity to reach target
      exploded: false,
      particles: [],
      color,
      age: 0,
      fallingStartTime: null
    }
  }

  const explodeFirework = (firework: Firework) => {
    const particleCount = Math.random() * 40 + 60 // Doubled from 20+30 to 40+60
    for (let i = 0; i < particleCount; i++) {
      firework.particles.push(createParticle(firework.x, firework.y, firework.color))
    }
    firework.exploded = true
  }

  const updateFireworks = (canvas: HTMLCanvasElement) => {
    fireworksRef.current = fireworksRef.current.filter(firework => {
      if (!firework.exploded) {
        firework.x += firework.vx
        firework.y += firework.vy
        firework.vy += 0.2 // gravity
        firework.age++ // Increment age

        // Check if firework just started falling (velocity became positive)
        if (firework.vy >= 0 && firework.fallingStartTime === null) {
          firework.fallingStartTime = firework.age
        }

        // Explosion conditions:
        // 1. Half second (30 frames at 60fps) after starting to fall
        // 2. Safety net - firework has been alive for too long (180 frames = ~3 seconds)
        const halfSecondAfterFalling = firework.fallingStartTime !== null &&
          (firework.age - firework.fallingStartTime) >= 30

        if (halfSecondAfterFalling || firework.age > 180) {
          explodeFirework(firework)
        }
        return true
      } else {
        firework.particles = firework.particles.filter(particle => {
          particle.x += particle.vx
          particle.y += particle.vy
          particle.vy += 0.1 // gravity
          particle.vx *= 0.99 // air resistance
          particle.vy *= 0.99
          particle.life--
          return particle.life > 0
        })
        return firework.particles.length > 0
      }
    })
  }

  const drawFireworks = (ctx: CanvasRenderingContext2D) => {
    fireworksRef.current.forEach(firework => {
      if (!firework.exploded) {
        // Draw rocket
        ctx.fillStyle = firework.color
        ctx.beginPath()
        ctx.arc(firework.x, firework.y, 3, 0, Math.PI * 2)
        ctx.fill()

        // Draw longer trail with gradient effect
        const gradient = ctx.createLinearGradient(
          firework.x, firework.y,
          firework.x - firework.vx * 8, firework.y - firework.vy * 8
        )

        // Slightly fade the trail when falling to indicate the delay before explosion
        const isFalling = firework.fallingStartTime !== null
        const trailOpacity = isFalling ? '80' : 'FF' // Slightly transparent when falling

        gradient.addColorStop(0, firework.color + trailOpacity)
        gradient.addColorStop(1, firework.color + '00') // Transparent end

        ctx.strokeStyle = gradient
        ctx.lineWidth = 3
        ctx.beginPath()
        ctx.moveTo(firework.x, firework.y)
        ctx.lineTo(firework.x - firework.vx * 8, firework.y - firework.vy * 8)
        ctx.stroke()
      } else {
        // Draw particles
        firework.particles.forEach(particle => {
          const alpha = particle.life / particle.maxLife
          ctx.fillStyle = particle.color + Math.floor(alpha * 255).toString(16).padStart(2, '0')
          ctx.beginPath()
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
          ctx.fill()
        })
      }
    })
  }

  const animate = (timestamp: number) => {
    const canvas = canvasRef.current
    if (!canvas || !isActive) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Clear canvas completely (transparent background)
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Create new fireworks more frequently
    if (timestamp - lastFireworkTime.current > 150 + Math.random() * 200) {
      fireworksRef.current.push(createFirework(canvas))
      lastFireworkTime.current = timestamp
    }

    updateFireworks(canvas)
    drawFireworks(ctx)

    if (isActive) {
      animationRef.current = requestAnimationFrame(animate)
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    if (isActive) {
      // Clear canvas completely (transparent)
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }

      fireworksRef.current = []
      lastFireworkTime.current = 0
      animationRef.current = requestAnimationFrame(animate)

      // Auto-complete after 4 seconds
      const timer = setTimeout(() => {
        if (onComplete) {
          onComplete()
        }
      }, 4000)

      return () => {
        clearTimeout(timer)
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current)
        }
        window.removeEventListener('resize', resizeCanvas)
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      window.removeEventListener('resize', resizeCanvas)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, onComplete])

  if (!isActive) return null

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1000
      }}
    />
  )
}

export default Fireworks